import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Search, X, ChevronDown } from 'lucide-react';
import SearchBar from './SearchBar';
import { useAuth } from '../contexts/AuthContext';

const categories = [
  { id: 'home', name: 'Home', path: '/' },
  { id: 'latest', name: 'Latest', path: '/latest' },
  { id: 'politics', name: 'Politics', path: '/category/politics' },
  { id: 'tech', name: 'Technology', path: '/category/tech' },
  { id: 'business', name: 'Business', path: '/category/business' },
  { id: 'culture', name: 'Culture', path: '/category/culture' },
  { id: 'entertainment', name: 'Entertainment', path: '/category/entertainment' },
  { id: 'about', name: 'About', path: '/about' },
];

const moreLinks = [
  { name: 'Shop', path: '/shop' },
  { name: 'Contact', path: '/contact' },
  { name: 'Subscribe', path: '/subscribe' },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const { user } = useAuth();


  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCtrlK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isCtrlK) {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
      if (e.key === '/') {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName?.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea' && !target?.isContentEditable) {
          e.preventDefault();
          setIsSearchOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);

      if (currentScrollY > lastScrollY && currentScrollY > 200) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsMoreOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMenuOpen || isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, isSearchOpen]);

  return (
    <>
      <header
        className={`fixed w-full z-50 transition-all duration-500 bg-white/95 backdrop-blur-xl shadow-lg ${
          hidden && !isMenuOpen ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="flex-shrink-0 relative z-10"
              aria-label="Clarity News Home"
            >
              <img
                src="https://ighpjdpsntdcsdvqnipc.supabase.co/storage/v1/object/public/claritynews/Clarity%20News%20Logo.png"
                alt="Clarity News"
                className="h-12 transition-all duration-300"
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-1">
              {categories.map((category) => {
                const isActive = location.pathname === category.path;
                return (
                  <Link
                    key={category.id}
                    to={category.path}
                    className={`relative px-4 py-2 text-sm font-semibold transition-all duration-300 rounded-full ${
                      isActive
                        ? 'text-[#B6AC7D] bg-[#B6AC7D]/10'
                        : 'text-gray-600 hover:text-[#B6AC7D] hover:bg-gray-50'
                    }`}
                  >
                    {category.name}
                  </Link>
                );
              })}

              <div className="relative">
                <button
                  onClick={() => setIsMoreOpen(!isMoreOpen)}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-semibold transition-all duration-300 rounded-full text-gray-600 hover:text-[#B6AC7D] hover:bg-gray-50"
                  aria-expanded={isMoreOpen}
                  aria-haspopup="true"
                >
                  More
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isMoreOpen ? 'rotate-180' : ''}`} />
                </button>

                {isMoreOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsMoreOpen(false)}
                    />
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-2xl shadow-2xl py-2 z-50 animate-scale-in">
                      {moreLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#B6AC7D] transition-colors"
                          onClick={() => setIsMoreOpen(false)}
                        >
                          {link.name}
                        </Link>
                      ))}
                      {user && (
                        <>
                          <div className="border-t border-gray-100 my-2" />
                          <Link
                            to="/account"
                            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#B6AC7D] transition-colors"
                            onClick={() => setIsMoreOpen(false)}
                          >
                            My Account
                          </Link>
                          <Link
                            to="/writer"
                            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#B6AC7D] transition-colors"
                            onClick={() => setIsMoreOpen(false)}
                          >
                            Writer Dashboard
                          </Link>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="group p-3 rounded-full transition-all duration-300 text-gray-600 hover:bg-gray-100 hover:text-[#B6AC7D] relative"
                aria-label="Search (Ctrl+K)"
              >
                <Search className="w-5 h-5" />
                <span className="hidden lg:flex absolute -bottom-1 -right-1 items-center justify-center px-1 py-0.5 rounded bg-gray-200 text-[10px] text-gray-500 font-mono leading-none group-hover:bg-[#B6AC7D]/20 group-hover:text-[#B6AC7D] transition-colors">/</span>
              </button>

              {user ? (
                <Link
                  to="/account"
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 bg-gray-100 text-gray-700 hover:bg-[#B6AC7D]/10 hover:text-[#B6AC7D]"
                >
                  <img
                    src={user.user_metadata?.avatar_url || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face'}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover"
                  />
                  <span className="max-w-[80px] truncate">{user.email?.split('@')[0]}</span>
                </Link>
              ) : (
                <Link
                  to="/subscribe"
                  className="hidden sm:flex items-center px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 bg-[#B6AC7D] text-white hover:bg-[#A49C6D] shadow-lg shadow-[#B6AC7D]/20 hover:shadow-xl hover:shadow-[#B6AC7D]/30"
                >
                  Subscribe
                </Link>
              )}

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-3 rounded-full transition-all duration-300 text-gray-600 hover:bg-gray-100"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-white transition-all duration-500 lg:hidden ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="h-20" />
        <div className="container mx-auto px-4 py-8 overflow-y-auto max-h-[calc(100vh-5rem)]">
          <nav className="space-y-2">
            {categories.map((category, index) => {
              const isActive = location.pathname === category.path;
              return (
                <Link
                  key={category.id}
                  to={category.path}
                  className={`block py-4 px-6 rounded-2xl text-lg font-semibold transition-all duration-300 animate-slide-in-left ${
                    isActive
                      ? 'bg-[#B6AC7D]/10 text-[#B6AC7D]'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-[#B6AC7D]'
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {category.name}
                </Link>
              );
            })}

            <div className="pt-4 border-t border-gray-100 mt-4">
              {moreLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block py-4 px-6 rounded-2xl text-lg font-semibold text-gray-700 hover:bg-gray-50 hover:text-[#B6AC7D] transition-colors animate-slide-in-left"
                  style={{ animationDelay: `${(categories.length + index) * 0.05}s` }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {user && (
              <div className="pt-4 border-t border-gray-100 mt-4">
                <Link
                  to="/account"
                  className="block py-4 px-6 rounded-2xl text-lg font-semibold text-gray-700 hover:bg-gray-50 hover:text-[#B6AC7D] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  My Account
                </Link>
                <Link
                  to="/writer"
                  className="block py-4 px-6 rounded-2xl text-lg font-semibold text-gray-700 hover:bg-gray-50 hover:text-[#B6AC7D] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Writer Dashboard
                </Link>
              </div>
            )}

            <div className="pt-6">
              <Link
                to="/subscribe"
                className="block w-full py-4 bg-[#B6AC7D] text-white text-center rounded-full font-semibold text-lg hover:bg-[#A49C6D] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Subscribe Now
              </Link>
            </div>
          </nav>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-50 transition-all duration-500 ${
          isSearchOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsSearchOpen(false)}
        />
        <div className={`relative bg-white shadow-2xl transition-transform duration-500 ${
          isSearchOpen ? 'translate-y-0' : '-translate-y-full'
        }`}>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Search</h2>
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close search"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
              <SearchBar
                key={isSearchOpen ? 'open' : 'closed'}
                variant="hero"
                autoFocus={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
